import { useEffect, useRef, useState } from "react";
import { Trophy, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { MaskedEmail } from "@/components/MaskedEmail";

const CONFETTI_COLORS = ["#22ff66", "#ffffff", "#ffd700", "#4ade80", "#a3e635"];
const ITEM_H = 110;

function Confetti() {
  const pieces = Array.from({ length: 70 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 2.5,
    duration: 3 + Math.random() * 3,
    size: 6 + Math.random() * 8,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    rotate: Math.random() * 360,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="ufr-confetti"
          style={{
            left: `${p.left}%`, width: p.size, height: p.size * 0.45, background: p.color,
            animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`, transform: `rotate(${p.rotate}deg)`,
          }}
        />
      ))}
    </div>
  );
}

export const WinnerReveal = ({ open, compTitle, winner, ticketPool, onClose }) => {
  const [stage, setStage] = useState("spin"); // spin | winner
  const [strip, setStrip] = useState([]);
  const [offset, setOffset] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    if (!open) return;
    setStage("spin");
    const pool = ticketPool && ticketPool.length ? [...ticketPool] : Array.from({ length: 60 }, (_, i) => i + 1);
    // Build the reel: ~45 shuffled numbers, winner placed near the end
    const items = [];
    while (items.length < 44) {
      const shuffled = [...pool].sort(() => Math.random() - 0.5).filter((n) => n !== winner.ticket_number);
      items.push(...(shuffled.length ? shuffled : [winner.ticket_number]));
    }
    const reel = items.slice(0, 44);
    reel.push(winner.ticket_number); // index 44
    reel.push(...reel.slice(0, 2)); // padding below
    setStrip(reel);
    setOffset(0);

    const t = timers.current;
    const startId = setTimeout(() => setOffset(-(44 - 1) * ITEM_H), 150); // winner lands in centre slot
    const winId = setTimeout(() => setStage("winner"), 8200);
    t.push(() => clearTimeout(startId), () => clearTimeout(winId));
    return () => { t.forEach((fn) => fn()); timers.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center" data-testid="winner-reveal-overlay">
      <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse at center, rgba(34,255,102,0.18) 0%, transparent 65%)" }} />
      {stage === "winner" && <Confetti />}

      <div className="relative text-center px-6 w-full max-w-2xl">
        <div className="flex justify-center mb-5"><Logo size={60} /></div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-bold">{compTitle}</div>
        <div className="text-[13px] uppercase tracking-[0.35em] text-[color:var(--ufr-green)] font-bold mt-5 ufr-pulse-text">
          {stage === "spin" ? "Drawing the winning ticket…" : "We have a winner!"}
        </div>

        {/* Ticket wheel */}
        <div className={`relative mx-auto mt-8 w-72 overflow-hidden rounded-md border border-white/10 bg-black/60 ${stage === "winner" ? "opacity-30" : ""}`} style={{ height: ITEM_H * 3 }} data-testid="winner-reveal-wheel">
          <div className="absolute inset-x-0 top-0 h-[110px] bg-gradient-to-b from-black to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-[110px] bg-gradient-to-t from-black to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 z-10 pointer-events-none rounded-md border-2 border-[color:var(--ufr-green)]" style={{ height: ITEM_H, boxShadow: "0 0 28px rgba(34,255,102,0.35), inset 0 0 22px rgba(34,255,102,0.12)" }} />
          <div
            style={{ transform: `translateY(${offset}px)`, transition: offset !== 0 ? "transform 7.4s cubic-bezier(0.12, 0.82, 0.04, 1)" : "none" }}
            data-testid="winner-reveal-strip"
          >
            {strip.map((n, i) => (
              <div key={i} className="flex items-center justify-center font-display text-6xl text-white/90" style={{ height: ITEM_H }}>
                #{n}
              </div>
            ))}
          </div>
        </div>

        {/* Winner popup */}
        {stage === "winner" && (
          <div className="fixed inset-0 z-20 flex items-center justify-center px-6" data-testid="winner-reveal-winner">
            <div className="ufr-winner-in relative w-full max-w-xl ufr-card rounded-lg border-[color:var(--ufr-green)]/40 bg-black/95 p-10 text-center" style={{ boxShadow: "0 0 60px rgba(34,255,102,0.25)" }}>
              <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="winner-reveal-close-btn">
                <X size={22} />
              </button>
              <div className="flex items-center justify-center gap-3">
                <Trophy size={26} className="text-[#ffd700]" />
                <div className="text-[12px] uppercase tracking-[0.4em] text-[#ffd700] font-bold">Winning ticket #{winner.ticket_number}</div>
                <Trophy size={26} className="text-[#ffd700]" />
              </div>
              <div className="font-display text-5xl md:text-7xl text-white mt-6 glow-text" data-testid="winner-reveal-name">
                {winner.user_name || "Winner"}
              </div>
              <div className="mt-5 text-sm text-white/60">
                <MaskedEmail email={winner.user_email} className="text-white/60 text-sm" testid="winner-reveal-email" />
              </div>
              <div className="text-white/60 text-sm mt-6 tracking-wide">Congratulations! 🎉 The card is yours.</div>
              <button onClick={onClose} className="btn-ufr-primary px-8 py-3 text-sm mt-8" data-testid="winner-reveal-done-btn">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
